/**
 * Vision AI Automation - Modern SaaS Experience
 * Advanced Interactions & Smooth Animations
 */

// Import styles
import '../css/styles.css';

// Mobile Menu Toggle
function initMobileMenu() {
    const menuBtn = document.getElementById('menuBtn');
    const mobileMenu = document.getElementById('mobileMenu');
    if (!menuBtn || !mobileMenu) return;

    menuBtn.addEventListener('click', () => {
        const isHidden = mobileMenu.classList.toggle('hidden');
        menuBtn.setAttribute('aria-expanded', !isHidden);
    });

    mobileMenu.querySelectorAll('a').forEach(link => {
        link.addEventListener('click', () => {
            mobileMenu.classList.add('hidden');
            menuBtn.setAttribute('aria-expanded', 'false');
        });
    });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !mobileMenu.classList.contains('hidden')) {
            mobileMenu.classList.add('hidden');
            menuBtn.setAttribute('aria-expanded', 'false');
        }
    });
}

// Smooth Scroll
function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', function (e) {
            const href = this.getAttribute('href');
            if (href !== '#') {
                e.preventDefault();
                const target = document.querySelector(href);
                if (target) {
                    window.scrollTo({
                        top: target.offsetTop - 80,
                        behavior: 'smooth'
                    });
                }
            }
        });
    });
}

// Navbar Scroll Effect - Enhanced for SaaS
function initNavbarScroll() {
    const nav = document.querySelector('nav');
    if (!nav) return;
    
    let lastScroll = 0;
    window.addEventListener('scroll', () => {
        const currentScroll = window.pageYOffset;
        
        if (currentScroll > 50) {
            nav.classList.add('scrolled');
        } else {
            nav.classList.remove('scrolled');
        }
        
        lastScroll = currentScroll;
    }, { passive: true });
}

// Scroll Reveal Animations
function initScrollReveal() {
    const elements = document.querySelectorAll('.scroll-reveal, .glass-card, .service-card, .about-card, .feature-card, .why-card, .contact-info-card');
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('revealed');
                observer.unobserve(entry.target);
            }
        });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
    
    elements.forEach(el => observer.observe(el));
}

// Contact Form Handler
function initContactForm() {
    const form = document.getElementById('contactForm');
    if (!form) return;

    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        
        const submitBtn = form.querySelector('.form-submit');
        const msgDiv = document.getElementById('formMessage') || createMessageDiv();
        const data = {
            name: form.name.value.trim(),
            email: form.email.value.trim(),
            phone: form.phone?.value.trim() || '',
            subject: form.subject.value.trim(),
            message: form.message.value.trim()
        };

        // Validate
        if (!data.name || data.name.length < 2) {
            showMessage(msgDiv, 'Please enter your full name.', 'error');
            return;
        }
        if (!data.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
            showMessage(msgDiv, 'Please enter a valid email address.', 'error');
            return;
        }
        if (!data.subject || data.subject.length < 3) {
            showMessage(msgDiv, 'Please enter a subject.', 'error');
            return;
        }
        if (!data.message || data.message.length < 10) {
            showMessage(msgDiv, 'Please enter a message (at least 10 characters).', 'error');
            return;
        }

        // Submit
        submitBtn.disabled = true;
        submitBtn.innerHTML = 'Sending to WhatsApp...';
        showMessage(msgDiv, 'Sending your message to WhatsApp...', 'loading');

        try {
            // Format message for WhatsApp
            const whatsappMessage = `*New Contact Form Submission*\n\n*Name:* ${data.name}\n*Email:* ${data.email}\n*Phone:* ${data.phone || 'N/A'}\n*Subject:* ${data.subject}\n\n*Message:*\n${data.message}`;
            
            // Create WhatsApp link
            const whatsappLink = `https://wa.me/447733830503?text=${encodeURIComponent(whatsappMessage)}`;
            
            // Open WhatsApp in new window
            window.open(whatsappLink, '_blank');
            
            setTimeout(() => {
                showMessage(msgDiv, '✓ Message sent to WhatsApp! We\'ll respond shortly.', 'success');
                form.reset();
                submitBtn.disabled = false;
                submitBtn.innerHTML = 'Send Message';
                setTimeout(() => msgDiv.style.display = 'none', 5000);
            }, 1000);
        } catch (error) {
            showMessage(msgDiv, 'Error sending message. Please contact us at +44 7733 830503 on WhatsApp.', 'error');
            submitBtn.disabled = false;
            submitBtn.innerHTML = 'Send Message';
        }
    });
}

function createMessageDiv() {
    const div = document.createElement('div');
    div.id = 'formMessage';
    div.style.display = 'none';
    document.getElementById('contactForm').appendChild(div);
    return div;
}

function showMessage(div, message, type) {
    div.textContent = message;
    div.className = `form-message ${type}`;
    div.style.display = 'block';
    if (type === 'error') {
        setTimeout(() => div.style.display = 'none', 5000);
    }
}

// WhatsApp Floating Button
function initWhatsAppButton() {
    const btn = document.createElement('a');
    btn.className = 'whatsapp-float';
    btn.href = 'https://wa.me/447733830503?text=' + encodeURIComponent('Hello Vision AI Automation, I\'m interested in your services.');
    btn.target = '_blank';
    btn.rel = 'noopener noreferrer';
    btn.setAttribute('aria-label', 'Chat on WhatsApp');
    btn.innerHTML = `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>`;
    document.body.appendChild(btn);
}

// Initialize All
document.addEventListener('DOMContentLoaded', () => {
    console.log('%c🚀 Vision AI Automation', 'color: #00d4ff; font-size: 20px; font-weight: bold;');
    console.log('%cWebsite loaded successfully!', 'color: #a855f7;');
    
    initMobileMenu();
    initSmoothScroll();
    initNavbarScroll();
    initScrollReveal();
    initContactForm();
    initWhatsAppButton();
    
    setTimeout(() => document.body.classList.add('loaded'), 100);
});
